import React, { useState } from "react";
import { MapModal, Address } from "./MapModal";
import { useContent } from "../../hooks";

export type ClickableAddressProps = {
    address: Address;
    className?: string;
    children?: React.ReactNode;
    showIcon?: boolean;
    variant?: "default" | "inline" | "block";
};

export const ClickableAddress: React.FC<ClickableAddressProps> = ({
    address,
    className = "",
    children,
    showIcon = true,
    variant = "default",
}) => {
    const { common } = useContent();
    const mapTitle = common?.map?.title;
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Format address for display if no children provided
    const formatAddress = (addr: Address): string => {
        if (addr.fullAddress) {
            return addr.fullAddress;
        }
        const parts: string[] = [];
        if (addr.street) {
            parts.push(addr.street);
        }
        if (addr.postalCode) {
            parts.push(addr.postalCode);
        }
        if (addr.city) {
            parts.push(addr.city);
        }
        if (addr.country) {
            parts.push(addr.country);
        }
        return parts.join(", ");
    };

    const displayText = children || formatAddress(address);

    // Determine base classes based on variant
    const baseClasses = {
        default: "cursor-pointer hover:text-primary transition-colors inline-flex items-center gap-2",
        inline: "cursor-pointer hover:text-primary transition-colors inline-flex items-center gap-1",
        block: "cursor-pointer hover:text-primary transition-colors flex items-start gap-2",
    };

    const combinedClassName = `${baseClasses[variant]} ${className}`.trim();

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsModalOpen(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsModalOpen(true);
        }
    };

    return (
        <>
            <div
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                aria-label={`Open map for ${formatAddress(address)}`}
                className={combinedClassName}
            >
                {showIcon && (
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path
                            fillRule="evenodd"
                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                            clipRule="evenodd"
                        />
                    </svg>
                )}
                {typeof displayText === "string" ? <span>{displayText}</span> : displayText}
            </div>
            <MapModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} address={address} title={mapTitle} />
        </>
    );
};

export default ClickableAddress;
